import { bitcoinjs } from '@optimex-xyz/btc-script'

import { INetwork } from '@/types'
import { ensureHexPrefix, isBtcChain, isMobileDevice, SUPPORTED_NETWORK, WALLET_OKX } from '@/utils'
import { CustomError } from '@/utils/error'
import { sendLog } from '@/utils/logger'

import { toXOnly } from 'bitcoinjs-lib/src/psbt/bip371'
import { ethers } from 'ethers'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, OkxBitcoinWallet, PsbtSignOptions, TransactionOptions } from './types'

export class OkxWallet extends BaseWallet {
  name = 'OKX Wallet'
  key = WALLET_OKX
  private accountsChangedListenerBtc: Record<string, ((...args: unknown[]) => void) | null> = {}
  private disconnectListenerBtc: Record<string, ((...args: unknown[]) => void) | null> = {}
  private connectedAddresses: Record<string, string> = {}
  private compressedPublicKeys: Record<string, string> = {}
  private currentNetwork: string | null = null
  private disconnecting = false

  private redirectToOKXWallet(): void {
    window.location.href = `okx://wallet/dapp/url?dappUrl=${encodeURIComponent(window.location.href)}`
  }

  private download(): void {
    throw new CustomError(
      'OKX Wallet is not installed! <a target="_blank" class="underline" href="https://www.okx.com/download">Download wallet</a>'
    )
  }

  private checkWalletAvailability(): void {
    if (!this.isInstalled()) {
      this.download()
    }
  }

  async getAddress(networkId: string): Promise<string> {
    this.checkWalletAvailability()

    if (!window.okxwallet) {
      throw new CustomError('Provider is not available')
    }

    if (this.connectedAddresses[networkId]) return this.connectedAddresses[networkId]

    if (networkId === SUPPORTED_NETWORK.BTC) {
      const address = (await window.okxwallet.bitcoin.getAccounts())[0]
      this.connectedAddresses[networkId] = address
      this.compressedPublicKeys[networkId] = await window.okxwallet.bitcoin.getPublicKey()
      return address
    }

    if (networkId === SUPPORTED_NETWORK.BTC_TESTNET) {
      const data = await window.okxwallet.bitcoinTestnet.connect()
      const address = data.address
      this.connectedAddresses[networkId] = address
      this.compressedPublicKeys[networkId] = data.compressedPublicKey
      return address
    }
    return this.connectedAddresses[networkId]
  }

  isInstalled(): boolean {
    if (isMobileDevice()) {
      return true
    }
    return typeof window !== 'undefined' && 'okxwallet' in window && window.okxwallet !== undefined
  }

  async connectByNetwork(networkId: string) {
    switch (networkId) {
      case SUPPORTED_NETWORK.BTC: {
        return await window.okxwallet!.bitcoin.connect()
      }
      case SUPPORTED_NETWORK.BTC_TESTNET: {
        return await window.okxwallet!.bitcoinTestnet.connect()
      }
      default: {
        throw new Error('provider is not available')
      }
    }
  }

  async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (isMobileDevice() && !window.okxwallet) {
      this.redirectToOKXWallet()
      throw new CustomError('Redirecting to OKX Wallet...')
    }

    this.checkWalletAvailability()
    this.currentNetwork = networkId

    if (isBtcChain(networkId)) {
      const provider =
        networkId === SUPPORTED_NETWORK.BTC ? window.okxwallet?.bitcoin : window.okxwallet?.bitcoinTestnet
      if (provider) {
        this.setupEventListenersBtc(provider, networkId, options)
      }
    } else {
      throw new Error('provider is not available')
    }

    let resp = await this.connectByNetwork(networkId)
    // sometime okx return null => throw error => user try again => always failed.
    // => call disconnect && connect again will resolve
    if (resp === null) await this.disconnect(networkId)
    resp = await this.connectByNetwork(networkId)

    const { address, compressedPublicKey } = resp
    this.connectedAddresses[networkId] = address
    this.compressedPublicKeys[networkId] = compressedPublicKey

    return { address, networkInfo: options.network, compressedPublicKey }
  }

  private removeEventListenersBtc(provider: OkxBitcoinWallet, network: string): void {
    if (!provider) return

    if (this.accountsChangedListenerBtc[network]) {
      provider.off('accountsChanged', this.accountsChangedListenerBtc[network]!)
      this.accountsChangedListenerBtc[network] = null
    }

    if (this.disconnectListenerBtc[network]) {
      provider.off('disconnect', this.disconnectListenerBtc[network]!)
      this.disconnectListenerBtc[network] = null
    }
  }

  private setupEventListenersBtc(
    provider: OkxBitcoinWallet,
    network: string,
    { onAccountChanged, network: networkInfo, onDisconnected }: IWalletConnectOptions
  ): void {
    if (typeof provider?.on !== 'function') return

    this.removeEventListenersBtc(provider, network)

    // Create a proper callback that will be reused
    this.accountsChangedListenerBtc[network] = (...args: unknown[]) => {
      const accounts = args[0] as string[]
      if (accounts?.length) {
        this.connectedAddresses[network] = accounts[0]
        onAccountChanged?.({ address: accounts[0], networkInfo })
      }
    }

    this.disconnectListenerBtc[network] = () => {
      onDisconnected?.(this.key)
    }

    // Register the event listeners
    provider.on('accountsChanged', this.accountsChangedListenerBtc[network]!)
    provider.on('disconnect', this.disconnectListenerBtc[network]!)
  }

  async disconnect(networkId?: string): Promise<void> {
    if (this.disconnecting) return
    this.disconnecting = true
    this.checkWalletAvailability()

    if (networkId) {
      await this.disconnectNetwork(networkId)
    } else if (this.currentNetwork) {
      await this.disconnectNetwork(this.currentNetwork)
    }
    this.currentNetwork = null
    if (networkId && this.connectedAddresses[networkId]) {
      delete this.connectedAddresses[networkId]
    }
    if (networkId && this.compressedPublicKeys[networkId]) {
      delete this.compressedPublicKeys[networkId]
    }
    this.disconnecting = false
  }

  private async disconnectNetwork(network: string): Promise<void> {
    if (network === SUPPORTED_NETWORK.BTC) {
      await window.okxwallet!.bitcoin.disconnect?.()
      this.removeEventListenersBtc(window.okxwallet!.bitcoin, network)
      return
    }

    if (network === SUPPORTED_NETWORK.BTC_TESTNET) {
      await window.okxwallet!.bitcoinTestnet.disconnect?.()
      this.removeEventListenersBtc(window.okxwallet!.bitcoinTestnet, network)
      return
    }
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    sendLog({
      message: 'okx start send',
      data: { toAddress, amount, network: networkId, options },
      tags: this.key,
    })
    this.checkWalletAvailability()

    if (!window.okxwallet) {
      throw new CustomError('Provider is not available')
    }
    const okxOptions = options as { feeRate?: number }

    switch (networkId) {
      case SUPPORTED_NETWORK.BTC:
        return window.okxwallet!.bitcoin.sendBitcoin(toAddress, Number(amount), okxOptions)
      case SUPPORTED_NETWORK.BTC_TESTNET:
        return window.okxwallet!.bitcoinTestnet.sendBitcoin(toAddress, Number(amount), okxOptions)
      default: {
        throw new Error(`Unsupported network: ${networkId}`)
      }
    }
  }

  async signMessage(message: string, network?: string): Promise<string> {
    this.checkWalletAvailability()

    if (!window.okxwallet) {
      throw new CustomError('Provider is not available')
    }

    switch (network) {
      case SUPPORTED_NETWORK.BTC:
        return await window.okxwallet.bitcoin.signMessage(message)
      case SUPPORTED_NETWORK.BTC_TESTNET:
        return await window.okxwallet.bitcoinTestnet.signMessage(message)
      default: {
        throw new Error('Invalid response from provider')
      }
    }
  }

  async signPsbt(psbt: string, network: string, options?: PsbtSignOptions): Promise<string> {
    if (!isBtcChain(network)) {
      throw new CustomError(`Unsupported network for PSBT signing: ${network}`)
    }

    const hexPsbt = bitcoinjs.Psbt.fromBase64(psbt).toHex()

    if (options?.toSignInputs?.length) {
      options = {
        ...options,
        toSignInputs: options.toSignInputs.map((input) => ({
          ...input,
          publicKey:
            isMobileDevice() || !input.publicKey
              ? undefined
              : ethers.hexlify(toXOnly(ethers.getBytes(ensureHexPrefix(input.publicKey)))).slice(2),
        })),
      }
    }

    const provider = network === SUPPORTED_NETWORK.BTC ? window.okxwallet!.bitcoin : window.okxwallet!.bitcoinTestnet

    return await provider.signPsbt(hexPsbt, options)
  }

  async getCompressedPublicKey(networkId: string): Promise<string> {
    this.checkWalletAvailability()

    if (!window.okxwallet) {
      throw new CustomError('Provider is not available')
    }

    if (this.compressedPublicKeys[networkId]) {
      return this.compressedPublicKeys[networkId]
    }

    switch (networkId) {
      case SUPPORTED_NETWORK.BTC: {
        const { compressedPublicKey } = await window.okxwallet.bitcoin.connect()
        this.compressedPublicKeys[networkId] = compressedPublicKey
        return this.compressedPublicKeys[networkId]
      }
      case SUPPORTED_NETWORK.BTC_TESTNET: {
        const { compressedPublicKey } = await window.okxwallet.bitcoinTestnet.connect()
        this.compressedPublicKeys[networkId] = compressedPublicKey
        return this.compressedPublicKeys[networkId]
      }
      default: {
        throw new Error('Provider is not available')
      }
    }
  }

  isDetected(): boolean {
    return !!window.okxwallet
  }

  priority(): number {
    return window.okxwallet || this.isInstalled() ? 0 : 1
  }
}
